import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Save,
  Globe,
  ArrowLeft,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Upload,
  Loader2,
  Image as ImageIcon,
  Clock,
  MessageSquare,
} from 'lucide-react'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import LoadingSpinner from '../common/LoadingSpinner'
import { cn } from '../../utils/cn'
import { INDUSTRIES, DEFAULT_HOURS } from '../../utils/industrySchemas'
import { useAuth } from '../../context/AuthContext'

// ─── Field renderers ─────────────────────────────────────────────────────────

const TextField = ({ field, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
      {field.label}
    </label>
    <input
      type={field.type === 'url' ? 'url' : 'text'}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      className={cn(
        'h-9 w-full px-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
        'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
        'placeholder:text-neutral-400 dark:placeholder:text-neutral-500 text-sm transition-all'
      )}
    />
  </div>
)

const TextAreaField = ({ field, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
      {field.label}
    </label>
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      rows={4}
      className={cn(
        'w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-md',
        'bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100',
        'focus:ring-2 focus:ring-accent-500 focus:border-transparent',
        'placeholder:text-neutral-400 dark:placeholder:text-neutral-500 text-sm transition-all resize-y'
      )}
    />
  </div>
)

const ImageField = ({ field, value, onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('files', file)
      if (clientId) form.append('clientId', clientId)
      const res = await apiService.post('/media/upload', form)
      onChange(res.data.files?.[0]?.public_url)
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
        {field.label}
      </label>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative group w-20 h-20 rounded-lg overflow-hidden border border-neutral-200 dark:border-[#2f2b41]">
            <img src={value} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => onChange('')}
              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
            >
              <Trash2 className="h-4 w-4 text-white" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600 flex items-center justify-center">
            <ImageIcon className="h-6 w-6 text-neutral-400" />
          </div>
        )}
        <label className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all',
          'border border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f]',
          'hover:bg-neutral-50 dark:hover:bg-[#2a2640] text-neutral-700 dark:text-neutral-300',
          uploading && 'opacity-50 pointer-events-none'
        )}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? 'Uploading...' : 'Upload image'}
          <input type="file" accept="image/*" className="sr-only" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const MultiImageField = ({ field, value = [], onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUploading(true)
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const form = new FormData()
        form.append('files', file)
        if (clientId) form.append('clientId', clientId)
        const res = await apiService.post('/media/upload', form)
        return res.data.files?.[0]?.public_url
      }))
      onChange([...value, ...urls])
      toast.success(`${urls.length} image(s) uploaded`)
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const removeImage = (index) => onChange(value.filter((_, i) => i !== index))

  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
        {field.label}
      </label>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {value.map((url, i) => (
          <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-neutral-200 dark:border-[#2f2b41]">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => removeImage(i)}
              className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
            >
              <Trash2 className="h-4 w-4 text-white" />
            </button>
          </div>
        ))}
        <label className={cn(
          'aspect-square rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600',
          'flex flex-col items-center justify-center cursor-pointer transition-all',
          'hover:border-accent-400 hover:bg-accent-50 dark:hover:bg-accent-900/10',
          uploading && 'opacity-50 pointer-events-none'
        )}>
          {uploading ? <Loader2 className="h-5 w-5 animate-spin text-neutral-400" /> : <Plus className="h-5 w-5 text-neutral-400" />}
          <input type="file" accept="image/*" multiple className="sr-only" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const HoursField = ({ value = DEFAULT_HOURS, onChange }) => (
  <div>
    <div className="overflow-hidden rounded-lg border border-neutral-200 dark:border-[#2f2b41]">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-neutral-50 dark:bg-dark-400">
            <th className="text-left px-4 py-2.5 font-medium text-neutral-600 dark:text-neutral-400">Day</th>
            <th className="text-left px-4 py-2.5 font-medium text-neutral-600 dark:text-neutral-400">Status</th>
            <th className="text-left px-4 py-2.5 font-medium text-neutral-600 dark:text-neutral-400">Opens</th>
            <th className="text-left px-4 py-2.5 font-medium text-neutral-600 dark:text-neutral-400">Closes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 dark:divide-[#2f2b41]">
          {value.map((row, i) => (
            <tr key={row.day} className="bg-white dark:bg-[#221f2f]">
              <td className="px-4 py-2.5 font-medium text-neutral-900 dark:text-neutral-100">{row.day}</td>
              <td className="px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...value]
                    updated[i] = { ...row, open: !row.open }
                    onChange(updated)
                  }}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                    row.open
                      ? 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-300'
                      : 'bg-neutral-100 text-neutral-500 dark:bg-dark-400 dark:text-neutral-500'
                  )}
                >
                  {row.open ? 'Open' : 'Closed'}
                </button>
              </td>
              <td className="px-4 py-2.5">
                <input
                  type="time"
                  value={row.openTime}
                  disabled={!row.open}
                  onChange={e => {
                    const updated = [...value]
                    updated[i] = { ...row, openTime: e.target.value }
                    onChange(updated)
                  }}
                  className={cn(
                    'px-2 py-1 border border-neutral-200 dark:border-[#2f2b41] rounded-md text-sm',
                    'bg-white dark:bg-dark-400 text-neutral-900 dark:text-neutral-100',
                    'focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all',
                    !row.open && 'opacity-40 cursor-not-allowed'
                  )}
                />
              </td>
              <td className="px-4 py-2.5">
                <input
                  type="time"
                  value={row.closeTime}
                  disabled={!row.open}
                  onChange={e => {
                    const updated = [...value]
                    updated[i] = { ...row, closeTime: e.target.value }
                    onChange(updated)
                  }}
                  className={cn(
                    'px-2 py-1 border border-neutral-200 dark:border-[#2f2b41] rounded-md text-sm',
                    'bg-white dark:bg-dark-400 text-neutral-900 dark:text-neutral-100',
                    'focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all',
                    !row.open && 'opacity-40 cursor-not-allowed'
                  )}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)

// ─── List field (repeatable items) ────────────────────────────────────────────

const ListField = ({ fieldDef, value = [], onChange, clientId }) => {
  const [expanded, setExpanded] = useState(null)

  const addItem = () => {
    const newItem = {}
    fieldDef.subFields.forEach(sf => { newItem[sf.key] = sf.type === 'multi-image' ? [] : '' })
    const updated = [...value, newItem]
    onChange(updated)
    setExpanded(updated.length - 1)
  }

  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index))
    if (expanded === index) setExpanded(null)
  }

  const updateItem = (index, key, val) => {
    const updated = [...value]
    updated[index] = { ...updated[index], [key]: val }
    onChange(updated)
  }

  return (
    <div className="space-y-2">
      {value.map((item, i) => (
        <div key={i} className="border border-neutral-200 dark:border-[#2f2b41] rounded-xl overflow-hidden">
          {/* Item header */}
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 dark:bg-dark-400">
            <button
              type="button"
              onClick={() => setExpanded(expanded === i ? null : i)}
              className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              {expanded === i ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {item[fieldDef.subFields[0]?.key] || `Item ${i + 1}`}
            </button>
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="p-1 text-neutral-400 hover:text-error-500 transition-colors rounded-md hover:bg-error-50 dark:hover:bg-error-900/20"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Item fields */}
          {expanded === i && (
            <div className="p-4 space-y-4 bg-white dark:bg-[#221f2f]">
              {fieldDef.subFields.map(sf => (
                <div key={sf.key}>
                  {sf.type === 'text' || sf.type === 'url' ? (
                    <TextField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'textarea' ? (
                    <TextAreaField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'image' ? (
                    <ImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : sf.type === 'multi-image' ? (
                    <MultiImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <Button type="button" variant="outline" size="sm" onClick={addItem} className="w-full gap-2">
        <Plus className="h-4 w-4" />
        {fieldDef.addLabel || 'Add Item'}
      </Button>
    </div>
  )
}

// ─── Field dispatcher ─────────────────────────────────────────────────────────

const FieldRenderer = ({ field, value, onChange, clientId }) => {
  switch (field.type) {
    case 'text':
    case 'url':
      return <TextField field={field} value={value} onChange={onChange} />
    case 'textarea':
      return <TextAreaField field={field} value={value} onChange={onChange} />
    case 'image':
      return <ImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'multi-image':
      return <MultiImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'hours':
      return <HoursField value={value} onChange={onChange} />
    case 'list':
      return <ListField fieldDef={field} value={value} onChange={onChange} clientId={clientId} />
    default:
      return null
  }
}

// ─── Main ContentEditor ────────────────────────────────────────────────────────

const ContentEditor = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const siteId = searchParams.get('site')

  const [activeTab, setActiveTab] = useState(null)
  const [content,   setContent]   = useState({})
  const [hasUnsaved, setHasUnsaved] = useState(false)

  // Load site + client data
  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId }
  )

  const { data: clientProfile, isLoading: clientLoading } = useQuery(
    ['client-profile'],
    () => apiService.get('/clients/profile').then(r => r.data),
  )

  // Parse existing content from meta_data
  useEffect(() => {
    if (site?.meta_data) {
      try {
        const parsed = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : site.meta_data
        setContent(parsed || {})
      } catch {
        setContent({})
      }
    }
  }, [site])

  const industry = clientProfile?.industry || 'general'
  const schema   = INDUSTRIES[industry] || INDUSTRIES.general

  // Set first tab on schema load
  useEffect(() => {
    if (schema && !activeTab) setActiveTab(schema.tabs[0]?.id)
  }, [schema])

  const updateField = useCallback((key, value) => {
    setContent(prev => ({ ...prev, [key]: value }))
    setHasUnsaved(true)
  }, [])

  // Save mutation
  const saveMutation = useMutation(
    (meta) => apiService.put(`/sites/${siteId}`, { meta_data: JSON.stringify(meta) }),
    {
      onSuccess: () => {
        toast.success('Content saved!')
        setHasUnsaved(false)
        queryClient.invalidateQueries(['site', siteId])
      },
      onError: () => toast.error('Failed to save content'),
    }
  )

  // Publish mutation
  const publishMutation = useMutation(
    async () => {
      await saveMutation.mutateAsync(content)
      return apiService.post(`/sites/${siteId}/publish`)
    },
    {
      onSuccess: () => toast.success('Site published!'),
      onError:   () => toast.error('Failed to publish'),
    }
  )

  const handleSave = () => saveMutation.mutate(content)
  const handlePublish = () => publishMutation.mutate()

  if (siteLoading || clientLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading editor..." />
      </div>
    )
  }

  if (!siteId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-neutral-500 dark:text-neutral-400">No site selected.</p>
        <Button variant="outline" onClick={() => navigate('/client/sites')}>Back to Sites</Button>
      </div>
    )
  }

  const activeTabSchema = schema.tabs.find(t => t.id === activeTab)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/client/sites')}
            className="shrink-0 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 truncate">
              {site?.name || 'Edit Content'}
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              {schema.icon} {schema.label} — update your website content below
            </p>
          </div>
          {hasUnsaved && (
            <span className="shrink-0 text-xs text-warning-600 dark:text-warning-400 font-medium">• Unsaved</span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/client/requests/new')}
            className="gap-1.5 text-xs"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Request a Change</span>
            <span className="sm:hidden">Request</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={saveMutation.isLoading}
            className="gap-1.5"
          >
            {saveMutation.isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
          <Button
            size="sm"
            onClick={handlePublish}
            disabled={publishMutation.isLoading}
            className="gap-1.5"
          >
            {publishMutation.isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
            Publish
          </Button>
        </div>
      </div>

      {/* Mobile tab bar */}
      <div className="flex sm:hidden overflow-x-auto gap-1 pb-1 -mx-1 px-1">
        {schema.tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'shrink-0 px-3 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap',
              activeTab === tab.id
                ? 'bg-accent-600 text-white shadow-sm'
                : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-dark-400 hover:text-neutral-900 dark:hover:text-neutral-100'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex gap-6">
        {/* Sidebar tabs — desktop only */}
        <div className="hidden sm:block w-48 shrink-0">
          <nav className="space-y-1">
            {schema.tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                  activeTab === tab.id
                    ? 'bg-accent-600 text-white shadow-sm'
                    : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-dark-400 hover:text-neutral-900 dark:hover:text-neutral-100'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {activeTabSchema && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">{activeTabSchema.label}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {activeTabSchema.fields.map(field => (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={content[field.key]}
                    onChange={val => updateField(field.key, val)}
                    clientId={clientProfile?.id}
                  />
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default ContentEditor
